import { Input } from '@trackman/web-shared-components';
import { ServiceTypes } from 'constants/ServiceTypes';
import { observer } from 'mobx-react-lite';
import React, { FormEvent, useEffect, useState } from 'react';
import Autosuggest, {
  ChangeEvent,
  InputProps,
  RenderSuggestionParams,
  SuggestionSelectedEventData,
  SuggestionsFetchRequestedParams,
} from 'react-autosuggest';
import { useInjection } from 'services/ioc/ioc.react';
import { TeamStore } from 'stores/TeamStore';
import styled from 'styled-components';
import { Label, TeamSelectorWrapper } from './TeamSelectorStyles';

export interface ISearchTeam {
  id: string;
  name: string;
  shortName: string;
  externalIds?: string[];
}

export interface IProps {
  onSelectionChange: (selectedTeam?: ISearchTeam) => any;
  selectedTeam?: ISearchTeam;
  isDisabled?: boolean;
  inputValue: string;
  setInputValue: React.Dispatch<React.SetStateAction<string>>;
  isError: boolean;
  label: string;
}
export const TeamSelector: React.FC<IProps> = observer((props: IProps) => {
  const teamStore = useInjection<TeamStore>(ServiceTypes.TeamStore);
  const [suggestions, setSuggestions] = useState<ISearchTeam[]>([]);
  const { onSelectionChange, selectedTeam, isDisabled, inputValue, setInputValue, isError, label } = props;
  const [isLoading, setIsLoading] = useState(false);

  const fetchTeamsByKeyword = async () => {
    if (inputValue.length >= 3) {
      setSuggestions(getSuggestions(inputValue));
    }

    setIsLoading(false);

    if (suggestions.length === 0) {
      setListText('No results found.');
    } else {
      setListText('Type at least 3 letters to search');
    }
  };

  useEffect(() => {
    if (inputValue.length >= 3) {
      setIsLoading(true);
      setListText('Searching...');
    }

    const timeout = setTimeout(() => {
      if (inputValue.length >= 3) {
        fetchTeamsByKeyword();
      }
    }, 300);

    return () => {
      clearTimeout(timeout);
      setSuggestions([]);
      setListText('Type at least 3 letters to search');
    };
  }, [inputValue]);

  useEffect(() => {
    teamStore.initializeIndex();
    onSelectionChange(selectedTeam);
    setInputValue(selectedTeam ? selectedTeam.name : '');
  }, [selectedTeam]);

  const onSuggestionsFetchRequested = (params: SuggestionsFetchRequestedParams) => {
    setSuggestions(getSuggestions(params.value));
  };

  const onSuggestionsClearRequested = () => {
    setSuggestions([]);
  };

  const getSuggestions = (value: string, filterIds?: string) => {
    const inputValue = value.trim().toLowerCase();
    const inputLength = inputValue.length;

    return inputLength < 3 ? [] : teamStore.searchTeamsByName(inputValue, filterIds);
  };

  const getSuggestionValue = (suggestion: ISearchTeam) => suggestion.name;

  const renderSuggestion = (suggestion: ISearchTeam, params: RenderSuggestionParams) => {
    const tokens = params.query.split(' ').map((x) => x.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&'));
    let { name } = suggestion;
    tokens.forEach((token) => {
      const reg = new RegExp(`\\b(${token})`, 'gi');
      name = name.replace(reg, `<mark>$1</mark>`);
    });

    return (
      <div>
        <p dangerouslySetInnerHTML={{ __html: name }} />
      </div>
    );
  };

  const onChange = (event: FormEvent<any>, params: ChangeEvent) => {
    setInputValue(params.newValue);
  };

  const onSuggestionSelected = (event: FormEvent<any>, data: SuggestionSelectedEventData<ISearchTeam>) => {
    onSelectionChange(data.suggestion);
  };

  const Hint = styled.div`
    padding: 4px 8px 8px 8px;
    border-radius: 8px;
    box-shadow: 0px 5px 15px 0px rgba(0, 0, 0, 0.1);
    background: #fff;

    color: #a0a0a0;
    font-family: Lato;
    font-size: 13px;
    font-style: normal;
    font-weight: 400;
    line-height: 18.2px;
    z-index: 999;
    position: absolute;
    bottom: calc(0% - 47px);
    left: 0;
    width: 100%;
    height: 47px;
    display: flex;
    align-items: center;
  `;

  const [listVisible, setListVisible] = useState(false);
  const [listText, setListText] = useState('Type at least 3 letters to search');

  const renderInputComponent = (props: any) => (
    <Label>
      <Input {...props} />
      {listVisible && suggestions.length === 0 && <Hint>{listText}</Hint>}
    </Label>
  );

  const inputProps = {
    value: inputValue,
    onChange,
    width: '416px',
    disabled: isDisabled,
    label,
    error: isError,
    hint: isError ? 'This field cannot be empty' : '',
    onFocus: () => setListVisible(true),
    onBlur: () => setListVisible(false),
  } as InputProps<ISearchTeam>;

  return (
    <TeamSelectorWrapper>
      <Autosuggest
        suggestions={suggestions}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        getSuggestionValue={getSuggestionValue}
        renderSuggestion={renderSuggestion}
        inputProps={inputProps}
        onSuggestionSelected={onSuggestionSelected}
        renderInputComponent={renderInputComponent}
        shouldRenderSuggestions={(value: string) => value.length >= 3 && !isLoading}
        focusInputOnSuggestionClick={false}
      />
    </TeamSelectorWrapper>
  );
});
